/**
 * Portfolio projects service — Firestore CRUD for the `portfolioProjects`
 * collection. Part of the self-contained Portfolio module ("GitHub for LearnX
 * students"): a student authors PROJECTS (the artifact), each private while
 * being built.
 *
 * A project document (Phase 1 subset — mentor-review / public fields are
 * reserved and default-null until later phases):
 *   {
 *     ownerUid,          // FK → users/{uid}; the authoring student. Immutable.
 *     ownerFirstName,    // denormalized FIRST NAME ONLY (privacy: minors) —
 *                        // never the full name; used later on the public page.
 *
 *     title, description,
 *     type,              // 'live_url' | 'file' | 'code_snippet'
 *     deployedUrl,       // live running link (live_url only)
 *     repoUrl,           // optional source link (any type)
 *     content,           // raw code text (code_snippet only)
 *     contentLanguage,   // syntax hint for code_snippet
 *     sampleInput,       // example answers to the program's input() prompts
 *     dataFiles,         // [{ name, content }] small text files it can read
 *     file,              // { name, url, path, size, type } | null (file only)
 *     thumbnailUrl,      // preview image ('' when none → card placeholder)
 *     techTags,          // string[] display chips
 *
 *     status,            // lifecycle — Phase 1 only ever 'draft'
 *     visibility,        // 'private' | 'public' — Phase 1 always 'private'
 *     mentorId, mentorEmail,                 // reserved (Phase 2 review)
 *     mentorFeedback, reviewedAt, reviewedByUserId,  // reserved (Phase 2)
 *     publishedAt,       // reserved (Phase 3 public page)
 *     createdAt, updatedAt,
 *   }
 *
 * SECURITY — this is the storage boundary, so all student-authored text is
 * sanitized against XSS HERE before it is ever persisted (these fields surface
 * on public pages in a later phase). Rendering must additionally stay text-only
 * (never dangerouslySetInnerHTML); the two together are defense-in-depth.
 */
import firestoreService, {
  where,
  serverTimestamp,
  arrayUnion,
  Timestamp,
} from './firestore.service';
import {
  PROJECT_TYPE,
  PROJECT_STATUS,
  VISIBILITY,
  NOTE_KIND,
  LIMITS,
  DATA_FILE_NAME,
} from 'features/portfolio/constants';

const PORTFOLIO_PROJECTS = 'portfolioProjects';

// ---- Sanitizers ----------------------------------------------------------

/**
 * Strip control characters that never belong in stored text, but KEEP tab,
 * newline and carriage return so code/description formatting survives. Done by
 * char code (not a literal control-char regex) to keep the source clean and
 * portable. This is the authoritative XSS-hardening pass for free-text fields
 * (paired with text-only rendering in the UI as defense-in-depth).
 */
function stripControl(value = '') {
  let out = '';
  for (const ch of String(value ?? '')) {
    const code = ch.charCodeAt(0);
    if (code === 9 || code === 10 || code === 13) {
      out += ch; // tab, newline, carriage return
    } else if (code < 32 || code === 127) {
      // drop other C0 controls + DEL
    } else {
      out += ch;
    }
  }
  return out;
}

/** Clean a single-line/multi-line text field: de-control, trim, length-cap. */
function cleanText(value = '', max = 500) {
  return stripControl(value)
    .trim()
    .slice(0, max);
}

/**
 * Clean a user-supplied URL to a safe absolute http(s) URL, or '' if it isn't
 * one. This is the XSS-critical field: it blocks `javascript:`, `data:`,
 * `vbscript:` and other script-bearing schemes that would execute if later
 * placed in an href/src.
 */
function cleanUrl(value = '') {
  const raw = cleanText(value, LIMITS.url);
  if (!raw) return '';
  try {
    const url = new URL(raw);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
}

/** Normalize the free-form tech tags: clean, drop empties, dedupe, cap count. */
function cleanTags(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const tag of value) {
    const t = cleanText(tag, LIMITS.tag);
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= LIMITS.tags) break;
  }
  return out;
}

/** Build a feedback-thread entry. Uses a concrete Timestamp (arrayUnion forbids
 *  serverTimestamp) and a unique id so distinct notes never dedupe together. */
function makeNote({ authorRole, authorName, text, kind }) {
  const role = authorRole === 'mentor' ? 'mentor' : 'student';
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    authorRole: role,
    authorName: cleanText(authorName, 60) || (role === 'mentor' ? 'Mentor' : 'Student'),
    text: cleanText(text, LIMITS.feedback),
    kind: Object.values(NOTE_KIND).includes(kind) ? kind : NOTE_KIND.NOTE,
    at: Timestamp.now(),
  };
}

/**
 * Clean the data files a student bundles with a runnable snippet — small text
 * files (a CSV, a JSON, some sample text) the preview drops next to the program
 * so a visitor can run it without supplying anything of their own.
 *
 * The NAME is the security-relevant half: the runner writes these into the
 * sandbox's working directory, so a path-like name ("../main.py") would let a
 * project overwrite its own source, or worse, a shim module. Names are matched
 * against a strict pattern and dropped outright if they fail — never "cleaned"
 * into something that merely looks safe.
 */
function cleanDataFiles(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const name = cleanText(entry.name, LIMITS.dataFileName);
    const content = stripControl(entry.content ?? '').slice(0, LIMITS.dataFileContent);
    if (!name || !content.trim()) continue;
    if (!DATA_FILE_NAME.test(name)) continue;
    const key = name.toLowerCase();
    if (key === 'main.py' || seen.has(key)) continue; // never shadow the program
    seen.add(key);
    out.push({ name, content });
    if (out.length >= LIMITS.dataFiles) break;
  }
  return out;
}

/** Sanitize an uploaded-file metadata object (URL forced safe). */
function cleanFile(file) {
  if (!file || typeof file !== 'object') return null;
  const url = cleanUrl(file.url);
  if (!url) return null; // no safe URL → treat as no file
  return {
    name: cleanText(file.name, 200) || 'file',
    url,
    path: cleanText(file.path, 500),
    size: Number.isFinite(file.size) ? file.size : 0,
    type: cleanText(file.type, 120),
  };
}

/**
 * Whitelist + sanitize the student-editable fields, clearing whatever is
 * irrelevant to the chosen `type` so a switched type never leaves stale content
 * behind. Shared by create and update.
 */
function sanitizeEditable(input = {}) {
  const type = Object.values(PROJECT_TYPE).includes(input.type)
    ? input.type
    : PROJECT_TYPE.LIVE_URL;

  return {
    title: cleanText(input.title, LIMITS.title),
    description: cleanText(input.description, LIMITS.description),
    type,
    // deployedUrl is meaningful only for a live_url project.
    deployedUrl: type === PROJECT_TYPE.LIVE_URL ? cleanUrl(input.deployedUrl) : '',
    // repoUrl is optional on every type.
    repoUrl: cleanUrl(input.repoUrl),
    // content/language only for a code snippet.
    content: type === PROJECT_TYPE.CODE_SNIPPET ? cleanText(input.content, LIMITS.code) : '',
    contentLanguage:
      type === PROJECT_TYPE.CODE_SNIPPET ? cleanText(input.contentLanguage, 40) : '',
    // What the preview should feed a runnable snippet: the student's own
    // example answers to their input() prompts, and any small data files the
    // program reads. Both exist so a VISITOR sees the project work without
    // having to guess what to type or supply a file of their own.
    sampleInput:
      type === PROJECT_TYPE.CODE_SNIPPET ? cleanText(input.sampleInput, LIMITS.sampleInput) : '',
    dataFiles: type === PROJECT_TYPE.CODE_SNIPPET ? cleanDataFiles(input.dataFiles) : [],
    // file only for a file project.
    file: type === PROJECT_TYPE.FILE ? cleanFile(input.file) : null,
    thumbnailUrl: cleanUrl(input.thumbnailUrl),
    techTags: cleanTags(input.techTags),
  };
}

export const portfolioProjectsService = {
  /**
   * Realtime subscription to one student's own projects. Filtered server-side by
   * `ownerUid` (query-provable against the rules) and sorted client-side by most
   * recently updated so no composite index is needed.
   */
  subscribeByOwner(ownerUid, onData, onError) {
    return firestoreService.subscribeCollection(
      PORTFOLIO_PROJECTS,
      [where('ownerUid', '==', ownerUid)],
      (rows) => onData(sortByUpdated(rows)),
      onError
    );
  },

  get(id) {
    return firestoreService.getDocument(PORTFOLIO_PROJECTS, id);
  },

  // ---- Public reads (Phase 3) — unauthenticated portfolio pages ----------

  /**
   * Fetch a single owner's PUBLIC + APPROVED projects for their portfolio grid.
   * The query is filtered by all three fields so it's provable against the
   * public read rule (visibility == public && status == approved); sorted by
   * publish time client-side. A visitor can never pull a private/draft project.
   */
  async listPublicByOwner(ownerUid) {
    const rows = await firestoreService.getCollection(PORTFOLIO_PROJECTS, [
      where('ownerUid', '==', ownerUid),
      where('visibility', '==', VISIBILITY.PUBLIC),
      where('status', '==', PROJECT_STATUS.APPROVED),
    ]);
    return rows.sort((a, b) => {
      const ta = a.publishedAt?.toMillis?.() ?? a.updatedAt?.toMillis?.() ?? 0;
      const tb = b.publishedAt?.toMillis?.() ?? b.updatedAt?.toMillis?.() ?? 0;
      return tb - ta;
    });
  },

  /**
   * Fetch one project for a public project page, returning it only if it is
   * public + approved (defense-in-depth on top of the rules, which already deny
   * reads of private/draft docs to an unauthenticated visitor). Returns null
   * when missing, denied, or not publicly visible.
   */
  async getPublic(id) {
    let doc = null;
    try {
      doc = await firestoreService.getDocument(PORTFOLIO_PROJECTS, id);
    } catch {
      return null; // permission-denied (private/draft) surfaces as not-found
    }
    if (!doc) return null;
    const visible = doc.visibility === VISIBILITY.PUBLIC && doc.status === PROJECT_STATUS.APPROVED;
    return visible ? doc : null;
  },

  /**
   * Create a new project owned by `ownerUid`. Always born as a private draft —
   * the lifecycle/visibility defaults are set here (not client-supplied) and the
   * security rules enforce the same, so a project can never be created
   * pre-approved or public.
   */
  create(ownerUid, ownerFirstName, data) {
    return firestoreService.addDocument(PORTFOLIO_PROJECTS, {
      ...sanitizeEditable(data),
      ownerUid,
      ownerFirstName: cleanText(ownerFirstName, 60) || 'Student',
      status: PROJECT_STATUS.DRAFT,
      visibility: VISIBILITY.PRIVATE,
      // Reserved for later phases — declared up-front so documents have a stable
      // shape and rules/queries can rely on the fields existing.
      mentorId: null,
      mentorEmail: null,
      mentorFeedback: null,
      reviewedAt: null,
      reviewedByUserId: null,
      publishedAt: null,
      feedbackThread: [],
    });
  },

  /** Update the editable content of an existing project (status/visibility untouched). */
  update(id, data) {
    return firestoreService.updateDocument(PORTFOLIO_PROJECTS, id, sanitizeEditable(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(PORTFOLIO_PROJECTS, id);
  },

  // ---- Review & feedback (Phase 2) --------------------------------------

  /**
   * Realtime subscription to every project associated with a mentor (their
   * review queue + already-reviewed items). Filtered server-side by `mentorId`
   * (query-provable against the rules' ownsMentor scoping) and sorted
   * client-side. The caller buckets by status.
   */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      PORTFOLIO_PROJECTS,
      [where('mentorId', '==', mentorId)],
      (rows) => onData(sortByUpdated(rows)),
      onError
    );
  },

  /**
   * Student submits a draft (or a changes-requested project) for review,
   * associating the mentor who will review it. Stays private — publishing only
   * ever happens on mentor approval. (Owner-update path in the rules.)
   */
  requestReview(id, { mentorId, mentorEmail } = {}) {
    return firestoreService.updateDocument(PORTFOLIO_PROJECTS, id, {
      status: PROJECT_STATUS.READY_FOR_REVIEW,
      visibility: VISIBILITY.PRIVATE,
      mentorId: mentorId || null,
      mentorEmail: (mentorEmail || '').trim().toLowerCase() || null,
    });
  },

  /**
   * Mentor approves a project. `publish` (default true) also makes it public and
   * stamps `publishedAt`. Visibility can only become public here, alongside
   * status = approved — the "public requires approved" invariant, enforced again
   * in the security rules.
   */
  approve(id, { publish = true, reviewerUid, reviewerName } = {}) {
    const patch = {
      status: PROJECT_STATUS.APPROVED,
      visibility: publish ? VISIBILITY.PUBLIC : VISIBILITY.PRIVATE,
      reviewedAt: serverTimestamp(),
      reviewedByUserId: reviewerUid || null,
      feedbackThread: arrayUnion(
        makeNote({
          authorRole: 'mentor',
          authorName: reviewerName,
          text: publish ? 'Approved and published to your portfolio.' : 'Approved.',
          kind: NOTE_KIND.APPROVED,
        })
      ),
    };
    if (publish) patch.publishedAt = serverTimestamp();
    return firestoreService.updateDocument(PORTFOLIO_PROJECTS, id, patch);
  },

  /**
   * Mentor requests changes. Feedback text is required (the caller validates) and
   * recorded both as `mentorFeedback` and as a thread entry. Forces the project
   * back to private so it can't remain public while changes are pending.
   */
  requestChanges(id, { text, reviewerUid, reviewerName } = {}) {
    const clean = cleanText(text, LIMITS.feedback);
    return firestoreService.updateDocument(PORTFOLIO_PROJECTS, id, {
      status: PROJECT_STATUS.CHANGES_REQUESTED,
      visibility: VISIBILITY.PRIVATE,
      mentorFeedback: clean,
      reviewedAt: serverTimestamp(),
      reviewedByUserId: reviewerUid || null,
      feedbackThread: arrayUnion(
        makeNote({
          authorRole: 'mentor',
          authorName: reviewerName,
          text: clean,
          kind: NOTE_KIND.CHANGES_REQUESTED,
        })
      ),
    });
  },

  /**
   * Append a lightweight collaborative note to a project's feedback thread —
   * usable by the mentor or the student outside a formal review, so building a
   * project feels collaborative rather than pass/fail. No-op on empty text.
   */
  addFeedbackNote(id, { authorRole, authorName, text } = {}) {
    const clean = cleanText(text, LIMITS.feedback);
    if (!clean) return Promise.resolve();
    return firestoreService.addToArray(
      PORTFOLIO_PROJECTS,
      id,
      'feedbackThread',
      makeNote({ authorRole, authorName, text: clean, kind: NOTE_KIND.NOTE })
    );
  },
};

/** Most-recently-updated first; falls back to createdAt. Tolerant of nulls. */
function sortByUpdated(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
    const tb = b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
}

export default portfolioProjectsService;
